// Used in resource.component.html, singular-assignment.component.html, view-assignment.component.html, composite-assignment-submit.component.html,
// activity-preview-dialog.component.html, and readycheck-report-dialog.component.html

// Angular
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";

// RXJS/Store
import * as courseStore from "#core/teacher-classes/store";
import * as studentStore from "#student/student-shared/store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";

// Idle
import { Idle } from "@ng-idle/core";

// Services/Types/Helpers
import { LtiHelper } from "#core/helpers/lti.helper";
import { AssignmentHelper } from "#helpers/assignment.helper";
import { LocalStorage } from "#services/local-storage.service";
import {
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    PPUser,
} from "cli-types";

// Moment
import { Asset, assetTypeIds, Bundle, C2C, Edition, eReader, isBundle, ReadyCheck } from "@carnegie-learning/cure-types";
import { Lti, LTI_EDULASTIC_LAUNCH_TYPES, LTIService } from "@carnegie-learning/data-access-angular-passport";
import { User } from "@carnegie-learning/data-access-fido";
import moment from "moment";

@Component({
    selector: "lti-iframe",
    templateUrl: "./lti-iframe.component.html",
    styleUrls: ["./lti-iframe.component.scss"],
    standalone: false
})
export class LtiIframeComponent implements OnInit, OnDestroy, OnChanges {
    private ngUnsubscribe = new Subject();
    EDU_LTI_PATH = (<any>window).edulasticLtiPath;
    C2C_LTI_PATH = (<any>window).C2CLtiPath;
    C2C_SATELLITE_ID = (<any>window).C2CSatelliteId; // this is a constant generated by C2C
    assetData:  ReadyCheck | C2C;

    iframeHTML: string;
    showIframe = false;
    showLtiError = false;
    ercReady = false;
    ltiRequest: Lti;
    fidoUserData: User;
    selectedAssignment: Assignment | CompositeTaskAssignment;

    @Input() user: PPUser;
    @Input() student?: PPUser;
    @Input() assignment: Assignment | CompositeTaskAssignment;
    @Input() asset: C2C | eReader | ReadyCheck;
    @Input() course: Course;
    @Input() edition: Edition;
    @Input() studentAssignments: Assignment[];
    @Input() launchType: string;
    @Input() destination: string;
    @Input() readOnly = false;
    @Input() compositeTaskAssignment: CompositeTaskAssignment;

    @Output() submitEvent = new EventEmitter<
        CompositeTaskAssignment | Assignment | ReadyCheck | C2C
    >();
    @Output() readyEvent = new EventEmitter<
        CompositeTaskAssignment | Assignment | ReadyCheck | C2C
    >();

    constructor(
        private ltiService: LTIService,
        private ltiHelper: LtiHelper,
        private idle: Idle,
        private cdr: ChangeDetectorRef,
        private courses_store: Store<courseStore.CoursesState>,
        private localStorage: LocalStorage,
        private assignmentHelper: AssignmentHelper,
        private store_assignments: Store<studentStore.StudentCoursesState>
    ) {}

    ngOnInit() {
        // set fido user Data
        this.fidoUserData = this.localStorage.get("fidoUserProfile");
        if (this.assignment && this.assignment.asset) {
            this.assetData = <C2C>this.assignment.asset;
            this.loadIFrame();
        } else if (this.assignment && !this.asset) {
            this.courses_store
                .pipe(select(courseStore.getAssignmentsEntities))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((assignments) => {
                    const assignment = assignments.get(
                        this.assignment.assignmentID
                    );
                    if (assignment?.asset) {
                        this.assetData = <C2C>assignment.asset;
                        this.loadIFrame();
                    }
                });
        } else {
            this.assetData = <C2C>this.asset;
            this.loadIFrame();
        }

        this.store_assignments
            .pipe(select(studentStore.getSelectedAssignment))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((assignment) => {
                if (
                    (<CompositeTaskAssignment>this.assignment)
                        ?.compositeTaskAssignmentID &&
                    (<CompositeTaskAssignment>this.assignment)?.assetTypeId ===
                        assetTypeIds.READYCHECK
                ) {
                    const activity = <CompositeTaskAssignment>this.assignment;
                    const compositeAssignment =
                        assignment?.compositeTaskAssignments.find(
                            (c) =>
                                c.compositeTaskAssignmentID ===
                                activity.compositeTaskAssignmentID
                        );

                    this.selectedAssignment = <CompositeTaskAssignment>(
                        compositeAssignment
                    );
                } else {
                    this.selectedAssignment = this.assignment;
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["student"]) {
            this.loadIFrame();
        }
    }

    loadIFrame() {
        if (this.launchType === "edulastic") {
            this.getEdulasticLTIResponse();
        } else if (this.launchType === "edulastic-ready-check") {
            this.getEdulasticReadyCheckLTIResponse(this.destination);
            this.checkiFrameActivity();
        } else if (this.launchType === "c2c") {
            this.getC2CLtiResponse();
        } else {
            this.showLtiError = true;
        }
    }

    getEdulasticLTIResponse() {
        this.ltiRequest = {
            url: this.EDU_LTI_PATH,
            user_id: this.user.userGUID,
            firstName: this.fidoUserData.givenName,
            lastName: this.fidoUserData.surName,
            email: this.user.eAddress,
            role: "instructor",
            instance_name: "Edulastic",
            instance_guid: "Edulastic",
            info_product_family_code: "cli",
            destination: "edulastic",
            domain_id: "edulastic",
            context_id: "home",
            context_title: "home",
        };

        this.setProductFamilyCode();

        const response = this.ltiService
            .sendRequest(this.ltiRequest)
            .pipe(first(), takeUntil(this.ngUnsubscribe))
            .subscribe(
                (newResponse) => {
                    this.iframeHTML = newResponse.page;
                    this.showIframe = true;
                    this.cdr.detectChanges();
                },
                (error) => {
                    this.showLtiError = true;
                    this.cdr.detectChanges();
                }
            );

        return response;
    }

    generateEdulasticOutcomeServiceURL(
        assignment: Assignment | CompositeTaskAssignment
    ) {
        if ((<CompositeTaskAssignment>assignment).compositeTaskAssignmentID) {
            // dealing with composite task assignment, send composite task route
            return (
                (<any>window).apiHost +
                "/api/lti/edulastic/composite/" +
                (<CompositeTaskAssignment>assignment)
                    .compositeTaskAssignmentID +
                "/user/" +
                this.assignment.userID
            );
        } else {
            // dealing with non-composite assignment
            return (
                (<any>window).apiHost +
                "/api/lti/edulastic/assignment/" +
                assignment.assignmentID +
                "/user/" +
                this.assignment.userID
            );
        }
    }

    generateC2COutcomeServiceURL() {
        return (<any>window).apiHost + "/api/c2c/assignments";
    }

    getEdulasticReadyCheckLTIResponse(destination: string) {
        const readyCheck: ReadyCheck = <ReadyCheck>this.assetData || {};

        const user = this.localStorage.get("userProfile");

        // student assignment views:
        if (this.assignment && this.course) {
            let attempt = 1;
            // Known bug, if student was in progress and dueDate was changed, this won't work
            if (
                this.assignment?.gradeAttempts?.length &&
                !this.assignmentHelper.hasSubmission(this.assignment)
            ) {
                const grade =
                    this.assignment.gradeAttempts[
                        this.assignment.gradeAttempts.length - 1
                    ];
                attempt = grade.attempt;
            }
            const source_id =
                this.assignment.assignmentID +
                "_" +
                readyCheck.externalId +
                "_" +
                attempt;

            // TODO: see PAS-6335: we are adding 8 months to the dueDate because Edulastic disables any assessments
            // past their due date and will not update the dueDate on their side on each launch.
            const dueDate = moment(this.assignment.dueDate)
                    .add("8", "months")
                    .toDate(),
                assignedStudentId =
                    this.assignment.userID === this.user.userID
                        ? this.user.userGUID
                        : this.student?.userGUID;

            if (!this.fidoUserData) {
                this.fidoUserData = this.localStorage.get("fidoUserProfile");
            }

            this.ltiRequest = {
                url: this.EDU_LTI_PATH,
                user_id: this.user.userGUID,
                firstName: this.fidoUserData.givenName,
                lastName: this.fidoUserData.surName,
                role: this.user.roleLevel === 5 ? "student" : "instructor",
                instance_name: "Edulastic",
                instance_guid: "Edulastic",
                info_product_family_code: "cli",
                resource_link_id: readyCheck.externalId, // "testextid1",
                launch_presentation_return_url: "iframe",
                result_source_id: source_id,
                destination: destination, // enum LTI_EDULASTIC_LAUNCH_TYPES
                domain_id: "dmnCLIFlex",
                school_id: this.course.schoolID,
                context_id: this.course.courseID.toString(),
                context_title: this.course.courseTitle,
                due_date: dueDate.getTime().toString(),
                teacher_user_id: this.course.primaryTeacher.userGUID,
                custom_student_user_id: assignedStudentId
                    ? assignedStudentId
                    : this.assignment.student?.userGUID,
                custom_standard_state_code: this.course.standardSet
                    ? this.course.standardSet
                    : "CC",
                outcome_service_url: this.generateEdulasticOutcomeServiceURL(
                    this.assignment
                ),
                token: user ? user.edulasticToken : null,
            };
        } else if (destination === LTI_EDULASTIC_LAUNCH_TYPES.TEACHER_REPORT) {
            const assignment = this.getReadyCheckCourseAssignment();
            let attempt = 1;
            // Known bug, if student was in progress and dueDate was changed, this won't work
            if (
                assignment?.gradeAttempts?.length &&
                !this.assignmentHelper.hasSubmission(assignment)
            ) {
                const grade =
                    assignment?.gradeAttempts[
                        assignment?.gradeAttempts.length - 1
                    ];
                attempt = grade.attempt;
            }
            if (assignment) {
                const source_id =
                    assignment.assignmentID +
                    "_" +
                    readyCheck.externalId +
                    "_" +
                    attempt;
                const dueDate = moment(assignment.dueDate)
                    .add("8", "months")
                    .toDate();
                this.assignment = assignment;
                this.ltiRequest = {
                    url: this.EDU_LTI_PATH,
                    user_id: this.user.userGUID,
                    firstName: this.fidoUserData.givenName,
                    lastName: this.fidoUserData.surName,
                    role: this.user.roleLevel === 5 ? "student" : "instructor",
                    instance_name: "Edulastic",
                    instance_guid: "Edulastic",
                    info_product_family_code: "cli",
                    resource_link_id: readyCheck.externalId,
                    launch_presentation_return_url: "iframe",
                    destination: destination, // enum LTI_EDULASTIC_LAUNCH_TYPES
                    domain_id: "dmnCLIFlex",
                    school_id: this.course.schoolID,
                    context_id: this.course.courseID.toString(),
                    context_title: this.course.courseTitle,
                    due_date: dueDate.getTime().toString(),
                    result_source_id: source_id,
                    custom_standard_state_code: this.course.standardSet
                        ? this.course.standardSet
                        : "CC",
                    outcome_service_url:
                        this.generateEdulasticOutcomeServiceURL(assignment),
                    teacher_user_id: this.course.primaryTeacher.userGUID,
                    token: user ? user.edulasticToken : null,
                };
            }
        } else if (this.readOnly) {
            this.ltiRequest = {
                url: this.EDU_LTI_PATH,
                user_id: this.user.userGUID,
                firstName: this.fidoUserData.givenName,
                lastName: this.fidoUserData.surName,
                role: this.user.roleLevel === 5 ? "student" : "instructor",
                instance_name: "Edulastic",
                instance_guid: "Edulastic",
                info_product_family_code: "cli",
                resource_link_id: readyCheck.externalId,
                launch_presentation_return_url: "iframe",
                destination: destination, // enum LTI_EDULASTIC_LAUNCH_TYPES
                domain_id: "dmnCLIFlex",
                school_id: this.course?.schoolID,
                context_id: this.course?.courseID.toString(),
                context_title: this.course?.courseTitle,
                custom_standard_state_code: this.course?.standardSet
                    ? this.course.standardSet
                    : "CC",
            };
        } else {
            this.showLtiError = true;
            return;
        }

        this.ltiService
            .sendRequest(this.ltiRequest)
            .pipe(first(), takeUntil(this.ngUnsubscribe))
            .subscribe(
                (response) => {
                    this.iframeHTML = response.page;
                    this.showIframe = true;
                    this.cdr.detectChanges();
                },
                (error) => {
                    this.showLtiError = true;
                    this.cdr.detectChanges();
                }
            );
    }

    getC2CLtiResponse() {
        let asset = <C2C>this.assetData;
        const isBundleAsset =
            this.compositeTaskAssignment &&
            isBundle(<Asset>this.assignment.asset);
        if (isBundleAsset) {
            asset = <C2C>(
                (<Bundle>this.assignment.asset).tasks.find(
                    (t) => t.assetId === this.compositeTaskAssignment.assetId
                )
            );
        }
        this.ltiRequest = {
            url: this.C2C_LTI_PATH,
            user_id: this.user.userGUID,
            firstName: this.fidoUserData.givenName,
            lastName: this.fidoUserData.surName,
            email: this.user.eAddress,
            role: this.user.roleLevel === 5 ? "LEARNER" : "INSTRUCTOR",
            context_id: "tempCourse",
            custom_satellite_id: this.C2C_SATELLITE_ID,
            custom_content_item_id: asset.contentItemId,
            custom_course_content_item_id: asset.courseContentItemId,
            custom_course_resource_element_id: asset.courseResourceElementId,
        };
        if (!this.readOnly) {
            let sync_id = this.ltiHelper.getSyncID(this.assignment);
            if (isBundleAsset) {
                sync_id = this.ltiHelper.getSyncID(
                    this.compositeTaskAssignment
                );
            }
            this.ltiRequest = {
                ...this.ltiRequest,
                context_id: this.course?.courseGUID,
                ext_lti_assignment_id: sync_id,
                result_source_id: sync_id,
                outcome_service_url: this.generateC2COutcomeServiceURL(),
            };
        } else if (this.assignment) {
            this.ltiRequest = {
                ...this.ltiRequest,
                url: this.assignment.submissionUrl,
                context_id: this.course?.courseGUID,
            };
        }
        this.ltiService
            .sendRequest(this.ltiRequest)
            .pipe(first(), takeUntil(this.ngUnsubscribe))
            .subscribe(
                (response) => {
                    this.showIframe = true;
                    this.iframeHTML = response.page;
                    this.cdr.detectChanges();
                },
                (error) => {
                    this.showLtiError = true;
                    this.cdr.detectChanges();
                }
            );
    }

    getAsset(): ReadyCheck | C2C | eReader {
        return this.asset || this.assignment.asset;
    }

    getReadyCheckCourseAssignment():
        | Assignment
        | CompositeTaskAssignment
        | undefined {
        if (this.studentAssignments?.length) {
            return this.studentAssignments.find((assignment) => {
                if (assignment.compositeTaskAssignments) {
                    return assignment.compositeTaskAssignments.some(
                        (task) => task.asset?.assetId === this.asset.assetId
                    );
                } else {
                    return assignment.asset?.assetId === this.asset.assetId;
                }
            });
        }
    }

    emitSubmit(
        assignment:
            | Assignment
            | CompositeTaskAssignment
            | C2C
            | ReadyCheck
    ) {
        this.submitEvent.emit(assignment);
    }

    emitReady(
        assignment:
            | Assignment
            | CompositeTaskAssignment
            | C2C
            | ReadyCheck
    ) {
        this.readyEvent.emit(assignment);
    }

    setProductFamilyCode() {
        // preload language specific banks in Edulastic
        if (this.course.subjectId === "ESP") {
            this.ltiRequest.info_product_family_code = "cli-spanish";
        }
        if (this.course.subjectId === "LANG") {
            this.ltiRequest.info_product_family_code = "cli-language-arts";
        }
        if (this.course.subjectId === "FRA") {
            this.ltiRequest.info_product_family_code = "cli-wlf";
        }
        if (this.course.subjectId === "CHI") {
            this.ltiRequest.info_product_family_code = "cli-wlc";
        }
        if (this.course.subjectId === "COMP-SCI") {
            this.ltiRequest.info_product_family_code = "cli-cs";
        }
        if (this.course.subjectId === "MATH") {
            this.ltiRequest.info_product_family_code = (<eReader>(
                this.asset
            )).externalId;
        }
        if (this.edition && this.edition.edulasticFamilyCode) {
            this.ltiRequest.info_product_family_code =
                this.edition.edulasticFamilyCode;
        }
    }

    checkiFrameActivity() {
        const monitor = setInterval(() => {
            const elem = document.activeElement;
            if (elem && elem.tagName === "IFRAME") {
                this.idle.watch(); // keep session alive
                clearInterval(monitor);
                this.checkiFrameActivity();
            }
        }, 3000);
    }

    displayIframe(): boolean {
        return (
            this.showIframe &&
            (this.launchType !== "edulastic-ready-check" ||
                (this.launchType === "edulastic-ready-check" && this.ercReady))
        );
    }

    @HostListener("window:message", ["$event"])
    onPostedMessage(event: MessageEvent) {
        switch (event.data) {
            case "pulse":
                this.idle.watch(); // keep session alive
                break;
            case "submit":
                this.readOnly
                    ? this.emitSubmit(<C2C>this.asset)
                    : this.emitSubmit(this.assignment);
                break;
            case "ready":
                this.readOnly
                    ? this.emitReady(<C2C>this.asset)
                    : this.emitReady(this.assignment);
                break;
        }

        if (this.launchType === "edulastic-ready-check" && event.data) {
            // Edulastic-specific message when iFrame has loaded:
            const dataObj =
                typeof event.data === "object"
                    ? event.data
                    : JSON.parse(event.data);
            switch (dataObj?.type) {
                case "load":
                case "frame:load":
                    this.ercReady = true;
                    this.readOnly
                        ? this.emitReady(<ReadyCheck>this.asset)
                        : this.emitReady(this.selectedAssignment);
                    break;
                case "SUBMIT_ASSIGNMENT":
                    this.readOnly
                        ? this.emitSubmit(<ReadyCheck>this.asset)
                        : this.emitSubmit(this.selectedAssignment);
                    break;
                case "APP_REFRESH":
                    // refresh embedded iFrame
                    this.showIframe = false;
                    this.loadIFrame();
                    break;
                default:
                    if (event.type === "message" && !this.ercReady) {
                        this.ercReady = true;
                        this.readOnly
                            ? this.emitReady(<ReadyCheck>this.asset)
                            : this.emitReady(this.selectedAssignment);
                    }
            }
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
